'use client'

import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { GalleryHorizontalEnd, Layers } from 'lucide-react'
import { LazyRender } from '@/components/lazy-render'
import { useResourceCardVideo } from '@/components/resource-card/resource-card'
import { SelectionCheckbox } from '@/components/selection-checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { VideoPlayer } from '@/components/video-player'
import { ResearchAdCardHoverActions } from '@/features/research/research-ads/research-ad-card/research-ad-card-hover-actions'
import { useResearchAdsContext } from '@/features/research/research-ads/research-ads-context'
import { createImageUrl } from '@/utils/image/create-image-url'
import { cn } from '@/utils/styles/cn'
import { run } from 'shared'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { useState } from 'react'

type ResearchAdCardContentProps = {
    ad: IResearchedAd
    setExpandedVideo: (expanded: string | null) => void
    /** Image media areas open the preview on click when provided; videos keep their player. */
    onPreview?: () => void
    selectionMode?: boolean
    selected?: boolean
    onToggleSelect?: () => void
    onEnterSelection?: () => void
}

export const ResearchAdCardContent = ({
    ad,
    setExpandedVideo,
    onPreview,
    selectionMode = false,
    selected = false,
    onToggleSelect,
    onEnterSelection,
}: ResearchAdCardContentProps) => {
    const { config } = useResearchAdsContext()
    const { mediaHovered, videoPlayMode, shouldPlay } = useResourceCardVideo()
    // A CloudFront-signed public URL breaks (403) the moment `createImageUrl` appends `?width=…`,
    // so read-only surfaces (which carry signed URLs) render the raw URL — next/image still
    // optimizes it without touching the signature, exactly as the public media card does.
    const toDisplayUrl = (url: string) => (config.readOnly ? url : createImageUrl(url, { width: 400 }))
    // Mirrors the parent's expandedVideo just for the overlay: an expanded video owns the whole
    // frame, so the corner actions must disappear with the rest of the card chrome.
    const [isVideoExpanded, setIsVideoExpanded] = useState(false)

    // Two independent facts about the same ad: how many versions Meta groups under it, and how
    // many cards it runs. They sit in one cluster so an ad that is both reads as one row of facts.
    const hasVersions = Boolean(ad.collation && ad.collation.size >= 2)
    const hasCards = (ad.creative.cardCount ?? 0) >= 2

    return (
        <div className="w-full bg-primary/5 relative">
            {run(() => {
                if (ad.creative.mediaFile?.category === 'VIDEO') {
                    return (
                        <div
                            // overflow-hidden clips media to its reserved box: a dimension-less ad
                            // renders at its natural height and would otherwise spill over the
                            // metrics below. With real dimensions the box already fits, so this is
                            // inert.
                            className="w-full relative overflow-hidden"
                            style={{
                                // Always reserve height: a dimension-less video otherwise renders at
                                // zero and grows on metadata load, shifting the whole column.
                                aspectRatio:
                                    ad.creative.mediaFile.width && ad.creative.mediaFile.height
                                        ? `${ad.creative.mediaFile.width}/${ad.creative.mediaFile.height}`
                                        : '4 / 5',
                            }}
                        >
                            <LazyRender>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <VideoPlayer
                                        src={ad.creative.mediaFile?.url ?? ''}
                                        poster={toDisplayUrl(ad.creative.thumbnailFile?.url ?? '')}
                                        onExpandChange={(expanded) => {
                                            setIsVideoExpanded(expanded)
                                            setExpandedVideo(expanded ? ad.uuid : null)
                                        }}
                                        // Replace the player's default surface click (play/pause +
                                        // double-click expand). Preview open is ResourceCard.onOpen
                                        // via bubble; pause stays on the control bar only.
                                        onSurfaceClick={onPreview ? () => undefined : undefined}
                                        // Play mode comes from ResourceCard — Research defaults to
                                        // auto; flip RESOURCE_CARD_VIDEO_PLAY_MODE.research to hover
                                        // without touching this player wiring.
                                        autoPlay={videoPlayMode === 'auto'}
                                        playing={videoPlayMode === 'hover' ? shouldPlay : undefined}
                                        muted
                                        loop
                                        className="w-full h-full"
                                    />
                                </motion.div>
                            </LazyRender>
                        </div>
                    )
                }

                return (
                    <div
                        // overflow-hidden clips media to its reserved box: a dimension-less ad
                        // renders at its natural height and would otherwise spill over the metrics
                        // below. With real dimensions the box already fits, so this is inert.
                        className={cn('relative w-full overflow-hidden', onPreview && 'cursor-pointer')}
                        // Card-level ResourceCard.onOpen owns preview; don't double-fire from media.
                        style={{
                            // Always reserve height: a dimension-less image otherwise renders at
                            // zero and grows on load, shifting the whole column.
                            aspectRatio:
                                ad.creative.mediaFile?.width && ad.creative.mediaFile?.height
                                    ? `${ad.creative.mediaFile.width}/${ad.creative.mediaFile.height}`
                                    : '4 / 5',
                        }}
                    >
                        <LazyRender>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Image
                                    src={toDisplayUrl(ad.creative.mediaFile?.url ?? '')}
                                    alt={ad.creative.title ?? ''}
                                    width={ad.creative.mediaFile?.width ?? 800}
                                    height={ad.creative.mediaFile?.height ?? 600}
                                    className="w-full h-full object-contain"
                                />
                            </motion.div>
                        </LazyRender>
                    </div>
                )
            })}

            {selectionMode ? (
                <>
                    {/* One toggle surface over the whole media (also blocks the video player —
                        intended while selecting), with the checkbox on top of it. */}
                    <div
                        className="absolute inset-0 z-30 cursor-pointer"
                        onClick={(event) => {
                            event.stopPropagation()
                            onToggleSelect?.()
                        }}
                    />
                    {(mediaHovered || selected) && (
                        <div className="absolute left-2 top-2 z-40" onClick={(event) => event.stopPropagation()}>
                            <SelectionCheckbox selected={selected} onClick={() => onToggleSelect?.()} />
                        </div>
                    )}
                </>
            ) : (
                // Read-only surfaces carry no authenticated actions, so the whole overlay is gone.
                !isVideoExpanded &&
                !config.readOnly && (
                    <ResearchAdCardHoverActions ad={ad} visible={mediaHovered} onEnterSelection={onEnterSelection} />
                )
            )}

            {/* A group's version count. `pointer-events-none` so it never intercepts the preview
                click or the selection overlay it sits on top of, and gone while a video is
                expanded — an expanded video owns the whole box. */}
            {!isVideoExpanded && (hasVersions || hasCards) && (
                <div className="pointer-events-none absolute bottom-2 left-2 z-40 flex items-center gap-1.5">
                    {hasVersions && (
                        <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                                    <Layers className="h-3 w-3" />
                                    {ad.collation!.size} versions
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-64">
                                Meta groups {ad.collation!.size} identical ads under one creative. Open to step
                                through the versions.
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {hasCards && (
                        <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                                    <GalleryHorizontalEnd className="h-3 w-3" />
                                    {ad.creative.cardCount} cards
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-64">
                                This ad runs {ad.creative.cardCount} cards. Open to step through them.
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    )
}
